<template>
  <v-form ref="form" v-model="valid" method="post" @submit.prevent="onSubmit">
    <v-container class="px-0" fluid>
      <v-row>
        <v-col cols="12" lg="6" xl="3">
          <v-card flat tile>
            <v-card-title>Initial Setup</v-card-title>
            <v-card-subtitle>Identify application, methods, and dataset</v-card-subtitle>
            <v-card-text>
              <v-text-field v-model="name" required label="Name of prediction" clearable />
              <v-autocomplete v-model="application" :items="applications" item-text="name" item-value="slug" required return-object label="Select an application" clearable />
              <v-autocomplete required multiple v-model="methods" item-text="name" item-value="id" :items="appMethods" no-data-text="Select an application" return-object label="Select prediction method(s)" clearable />
              <v-autocomplete v-if="application && application.slug == 'predict'" v-model="ducks" :items="appTargets" multiple required label="Select target(s) for prediction" clearable />
              <v-autocomplete v-model="dataset" :items="datasets" item-text="name" item-value="uuid" required return-object label="Select a dataset" clearable />
              <v-text-field v-model.number="config.months" type="number" label="Months of forecast" hint="Specifies the number of months used for ARPS declines" clearable />
              <v-autocomplete label="Forecasting technique" :items="techniques" item-text="name" item-value="slug" v-model="config.techniques" required clearable multiple />
              <v-checkbox
                v-model="config.termination.enabled"
                label="Termination"
                persistent-hint
                hint="Terminate forecasts when annual decline rate hits this rate"
              ></v-checkbox>
              <v-row>
                <v-col>
                  <v-text-field v-model.number="config.termination.oil"
                                suffix="%"
                                type="number"
                                label="Oil"
                                hide-spin-buttons hide-details
                                :disabled="!config.termination.enabled" />
                </v-col>
                <v-col>
                  <v-text-field v-model.number="config.termination.gas"
                                suffix="%"
                                type="number"
                                label="Gas"
                                hide-spin-buttons hide-details
                                :disabled="!config.termination.enabled" />
                </v-col>
                <v-col>
                  <v-text-field v-model.number="config.termination.water"
                                suffix="%"
                                type="number"
                                label="Water"
                                hide-spin-buttons hide-details
                                :disabled="!config.termination.enabled" />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" lg="6" xl="3" v-if="dataset">
          <v-card flat tile>
            <v-card-title>Data columns</v-card-title>
            <v-card-subtitle>Map specific data columns required for output</v-card-subtitle>
            <v-card-text>
              <v-row>
                <v-col>
                  <v-autocomplete v-model="config.api" :items="columns" required clearable>
                    <template #label>
                      <span class="red--text"><strong>*</strong></span>
                      Unique well identifier
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col>
                  <v-autocomplete v-model="config.name" :items="columns" label="Well name or label" clearable />
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-autocomplete v-model="config.latitude" :items="columns" clearable>
                    <template #label>
                      <span class="red--text"><strong>*</strong></span>
                      Surface latitude
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col>
                  <v-autocomplete v-model="config.longitude" :items="columns" clearable>
                    <template #label>
                      <span class="red--text"><strong>*</strong></span>
                      Surface longitude
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-autocomplete v-model="config.date" :items="columns" required clearable>
                    <template #label>
                      <span class="red--text"><strong>*</strong></span>
                      Production date
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col>
                  <v-autocomplete v-model="config.targets.oil" :items="columns" required label="Monthly oil production" clearable />
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-autocomplete v-model="config.targets.gas" :items="columns" label="Monthly gas production" clearable />
                </v-col>
                <v-col>
                  <v-autocomplete v-model="config.targets.water" :items="columns" label="Monthly water production" clearable />
                </v-col>
              </v-row>

              <v-autocomplete
                multiple
                chips
                required
                v-model="config.filters"
                :items="filters"
                item-text="name"
                item-value="id"
                label="Select columns available for filtering"
                hint="These columns are used as filters within applications, and are are automatically included in the output."
                persistent-hint
                clearable
              ></v-autocomplete>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" lg="6" xl="6" v-if="dataset && !isEmpty(fields)">
          <v-card flat tile>
            <v-card-title>Feature mapping</v-card-title>
            <v-card-subtitle>Select data columns to use as features</v-card-subtitle>
            <v-card-text>
              <!-- <v-alert text type="info">Please note that the quality of predictions depends on available features. If your dataset does not have all features available, the quality of predictions will be lower. If you do not provide any feature mapping, only production data will be used to generate predictions.</v-alert> -->
              <v-row class="mb-4">
                <v-col v-for="(k, v, i) in fields" :key="i" cols="4">
                  <v-autocomplete
                    :disabled="k == 'Year'"
                    :hint="k == 'Year' ? 'Automatically calculated' : ''"
                    v-model="config.features[k]"
                    :label="v"
                    :items="columns"
                    hide-details clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <v-card-actions>
      <v-btn v-if="prediction" depressed color="success" type="submit" :disabled="!isValidForm">Update</v-btn>
      <v-btn v-else depressed color="success" @click="onSubmit" :disabled="!isValidForm">Generate</v-btn>
      <v-btn text @click="$router.go(-1)">Cancel</v-btn>
    </v-card-actions>
  </v-form>
</template>

<script>
import Datasets from "@/api/datasets"
export default {
  name: "PredictionForm",
  props: {
    applications: { type: Array, required: true },
    models: { type: Array, required: true },
    datasets: { type: Array, required: true },
    techniques: { type: Array, required: true },
    prediction: { type: Object, required: false }
  },
  data() {
    return {
      valid: false,
      name: null,
      application: null,
      methods: [],
      dataset: null,
      columns: [],
      filters: [],
      config: {
        api: null,
        latitude: null,
        longitude: null,
        date: null,
        targets: {
          oil: null,
          gas: null,
          water: null
        },
        filters: [],
        features: {},
        months: 600,
        techniques: [],
        termination: {
          enabled: false,
          strategy: 'decline',
          oil: null,
          gas: null,
          water: null
        },
      },
      ducks: [],
    }
  },
  watch: {
    dataset: function(val) {
      if (val) {
        Datasets.columns(this.dataset.uuid).then(response => {
          this.columns = response.data.sort()
          this.filters = response.data
        })
      }
    },
    "config.latitude": function() {
      this.config.features["Surface Latitude"] = this.config.latitude
    },
    "config.longitude": function() {
      this.config.features["Surface Longitude"] = this.config.longitude
    },
    application: function() {
      if (!this.prediction) {
        Object.keys(this.fields).forEach(f => {
          this.$set(this.config.features, f, null)
        })
      }
    },
    ducks: function() {
      this.config.targets.oil = this.ducks.includes("oil") ? "Oil" : null
      this.config.targets.gas = this.ducks.includes("gas") ? "Gas" : null
      this.config.targets.water = this.ducks.includes("water") ? "Water" : null
    },
    "config.termination.enabled": function(val) {
      if (!val) {
        this.config.termination.oil = null
        this.config.termination.gas = null
        this.config.termination.water = null
      }
    }
  },
  mounted() {
    if (this.prediction) {
      this.name = this.prediction.name
      this.application = this.prediction.application
      this.methods = this.prediction.methods
      this.dataset = this.prediction.dataset
      this.config = this.prediction.config
      this.config.months = this.config.months ? this.config.months : 600
      if (!('termination' in this.config)) {
        this.config.termination = {
          strategy: null,
          limit: null
        }
      }
      if (this.application.slug == "predict") {
        Object.entries(this.config.targets).forEach(([key, value]) => {
          if (value != null) {
            this.ducks.push(key)
          }
        })
      }
    }
  },
  computed: {
    isValidForm() {
      return this.isValidInitial && this.isValidColumns && this.isValidFeatures && this.isValidTermination
    },
    isValidInitial() {
      return !!this.name
          && !!this.application
          && !this.isEmpty(this.methods)
          && !!this.dataset
          && !!this.config.months
          && !this.isEmpty(this.config.techniques)
    },
    isValidColumns() {
      if (this.application.slug == "forecast") {
        return !!this.config.api
            && !!this.config.latitude
            && !!this.config.longitude
            && !!this.config.date
            && !!this.isValidTargets
      } else if (this.application.slug == "predict") {
        return !!this.config.api
            && !!this.config.latitude
            && !!this.config.longitude
      }
      return false
    },
    isValidFeatures() {
      return Object.entries(this.config.features).filter((k, v) => v != null).length > 0
    },
    isValidTargets() {
      return Object.values(this.config.targets).filter(x => x != null).length > 0
    },
    isValidTermination() {
      if (!('termination' in this.config)) return false
      if (!this.config.termination.enabled) return true
      return  !!this.config.termination.enabled ? 
                !!this.config.termination.oil && 
                !!this.config.termination.gas && 
                !!this.config.termination.water 
              : true
    },
    fields() {
      if (this.application) {
        let o = this.methods.map(m => m.fields).filter(f => f !== null)
        let x = {}
        o.forEach(e => {
          for (const [k, v] of Object.entries(e)) {
            x[k] = v
          }
        })
        return x
      }
      return {}
    },
    appTargets() {
      if (!this.methods || this.methods.length === 0) return []
      const targets = this.methods
        .filter(m => m.targets != null)
        .map(m => m.targets)
        .flat()
        .filter(t => t != null)
      return [...new Set(targets)].map(
        function(e) {
          return { text: this.capitalize(e), value: e }
        }.bind(this)
      )
    },
    appMethods() {
      return this.models
    },
    strategy_suffix() {
      if (!this.config.termination.strategy) return ''
      let strategy = this.strategies.find(s => s.value == this.config.termination.strategy)
      if (strategy && strategy.unit) return strategy.unit
      return ''
    },
    strategy_hint() {
      if (!this.config.termination.strategy) return ''
      let strategy = this.strategies.find(s => s.value == this.config.termination.strategy)
      if (strategy && strategy.hint) return strategy.hint
      return ''
    },
  },
  methods: {
    filterAllowed(options, value) {
      return options.filter((o) => !(o.value == value && !this.isSuper))
    },
    onSubmit() {
      this.config.features = Object.fromEntries(Object.entries(this.config.features).filter(([_, v]) => v != null))
      this.config.targets = Object.fromEntries(Object.entries(this.config.targets).filter(([_, v]) => v != null))
      delete this.config.termination.limit
      this.$emit("submitted", {
        name: this.name,
        application: this.application.id,
        methods: this.methods.map(m => m.id),
        dataset: this.dataset.id,
        config: this.config
      })
    }
  }
}
</script>